// ============================================================================
// LLM COSTS ADMIN PAGE - Cross-project cost overview + pricing management
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, TrendingUp, Zap, RefreshCw, ExternalLink, Plus, Pencil, Trash2, Save, X, Tag, Search } from 'lucide-react';
import {
  getAdminCostOverview,
  LiteLLMAdminOverview,
  getAllPricing,
  upsertPricing,
  deletePricing,
  seedPricing,
  LLMPricingEntry,
  formatCost,
  formatTokens,
} from '../services/llmCostsService';

const COLORS = ['#00c7c4', '#0891b2', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
const PROVIDERS = ['anthropic', 'openai', 'google', 'mistral', 'other'];

const LLMCostsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'pricing'>('overview');

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <DollarSign size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">LLM Costs</h1>
              <p className="text-slate-500 text-sm font-medium">Cross-project LLM execution cost tracking</p>
            </div>
          </div>
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-all ${
                activeTab === 'overview' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              Cost Overview
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-all ${
                activeTab === 'pricing' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              Model Pricing
            </button>
          </div>
        </header>

        {activeTab === 'overview' ? <CostOverviewTab navigate={navigate} /> : <PricingTab />}
      </div>
    </div>
  );
};

// ============================================================================
// COST OVERVIEW TAB
// ============================================================================

function CostOverviewTab({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const [data, setData] = useState<LiteLLMAdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminCostOverview();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCosts(); }, [fetchCosts]);

  const pieData = data ? data.byModel.map(m => ({ name: m.model, value: m.spend })) : [];

  return (
    <>
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={fetchCosts}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <RefreshCw size={20} className="animate-spin mr-2" /> Loading cost data...
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && data && data.byProject.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <DollarSign size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No LLM cost data recorded yet.</p>
        </div>
      )}

      {!loading && !error && data && data.byProject.length > 0 && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-3 gap-4">
            <SummaryCard icon={DollarSign} label="Total Spend (All Time)" value={formatCost(data.totalSpend)} />
            <SummaryCard icon={Zap} label="Total Requests" value={data.totalRequests.toString()} />
            <SummaryCard icon={TrendingUp} label="Projects with Usage" value={data.byProject.length.toString()} />
          </div>

          {pieData.length > 0 && (
            <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
              <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-4">Cost by Model</p>
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="25%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value">
                      {pieData.map((entry, i) => <Cell key={entry.name ?? i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number | undefined) => formatCost(v ?? 0)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-2 text-xs">
                  {data.byModel.map((m, i) => (
                    <div key={m.model} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-slate-600 truncate max-w-[200px]">{m.model}</span>
                      <span className="text-slate-400 ml-1">{m.requests} req</span>
                      <span className="text-slate-800 font-medium ml-auto">{formatCost(m.spend)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <ProjectTable projectEntries={data.byProject} navigate={navigate} />
        </div>
      )}
    </>
  );
}

// ============================================================================
// PROJECT TABLE
// ============================================================================

function ProjectTable({
  projectEntries,
  navigate,
}: {
  projectEntries: Array<{ projectId: string; spend: number; requests: number; totalTokens: number }>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [search, setSearch] = useState('');

  const filtered = projectEntries.filter(p =>
    p.projectId.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-[#e2e8f0]">
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Cost by Project</p>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter projects..."
            className="pl-8 pr-3 py-1.5 text-xs border border-[#e2e8f0] rounded-xl w-56 text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
            <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Project ID</th>
            <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Requests</th>
            <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Tokens</th>
            <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Total Spend</th>
            <th className="px-5 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e2e8f0]">
          {filtered.map(proj => (
            <tr key={proj.projectId} className="hover:bg-slate-50/60 transition-colors">
              <td className="px-5 py-3 font-medium text-navy truncate max-w-[220px]">{proj.projectId}</td>
              <td className="px-5 py-3 text-right text-slate-500">{proj.requests}</td>
              <td className="px-5 py-3 text-right text-slate-500">{formatTokens(proj.totalTokens)}</td>
              <td className="px-5 py-3 text-right font-semibold text-navy">{formatCost(proj.spend)}</td>
              <td className="px-5 py-3 text-right">
                <button
                  onClick={() => navigate(`/projects/${proj.projectId}?tab=costs`)}
                  className="text-primary hover:text-primary-dark transition-colors"
                  title="View project costs"
                >
                  <ExternalLink size={14} />
                </button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={5} className="px-5 py-6 text-center text-slate-400 text-xs">
                No projects match your search.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// PRICING MANAGEMENT TAB
// Note: LiteLLM now owns cost calculation. These entries are legacy config.
// ============================================================================

const EMPTY_FORM = { modelId: '', provider: 'anthropic', displayName: '', inputPer1M: 0, outputPer1M: 0, active: true };

function PricingTab() {
  const [entries, setEntries] = useState<LLMPricingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterProvider, setFilterProvider] = useState<string>('all');

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllPricing();
      setEntries(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  const handleSeed = async () => {
    try {
      setSaving(true);
      const count = await seedPricing();
      if (count > 0) await fetchPricing();
      else setError('Pricing table already has entries. Seed only works on empty tables.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNew = async () => {
    if (!addForm.modelId.trim()) return;
    setSaving(true);
    try {
      await upsertPricing(addForm.modelId, addForm);
      setShowAdd(false);
      setAddForm(EMPTY_FORM);
      await fetchPricing();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (entry: LLMPricingEntry) => {
    setEditingId(entry.modelId);
    setEditForm({
      modelId: entry.modelId,
      provider: entry.provider,
      displayName: entry.displayName,
      inputPer1M: entry.inputPer1M,
      outputPer1M: entry.outputPer1M,
      active: entry.active,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await upsertPricing(editingId, editForm);
      setEditingId(null);
      await fetchPricing();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (modelId: string) => {
    if (!window.confirm(`Delete pricing for "${modelId}"?`)) return;
    try {
      await deletePricing(modelId);
      await fetchPricing();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const filtered = filterProvider === 'all' ? entries : entries.filter(e => e.provider === filterProvider);
  const providers = [...new Set(entries.map(e => e.provider))].sort();

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
        LiteLLM now handles cost calculation directly. These pricing entries are legacy configuration and no longer used for billing.
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setFilterProvider('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                filterProvider === 'all' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              All ({entries.length})
            </button>
            {providers.map(p => (
              <button
                key={p}
                onClick={() => setFilterProvider(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all capitalize ${
                  filterProvider === p ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {p} ({entries.filter(e => e.provider === p).length})
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {entries.length === 0 && !loading && (
            <button
              onClick={handleSeed}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <Tag size={13} /> Seed Defaults
            </button>
          )}
          <button
            onClick={() => { setShowAdd(true); setAddForm(EMPTY_FORM); }}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Plus size={13} /> Add Model
          </button>
          <button onClick={fetchPricing} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <RefreshCw size={20} className="animate-spin mr-2" /> Loading pricing...
        </div>
      )}

      {!loading && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                <th className="text-left py-2.5 px-4 text-xs font-bold uppercase tracking-wider text-slate-400">Provider</th>
                <th className="text-left py-2.5 px-4 text-xs font-bold uppercase tracking-wider text-slate-400">Model ID</th>
                <th className="text-left py-2.5 px-4 text-xs font-bold uppercase tracking-wider text-slate-400">Display Name</th>
                <th className="text-right py-2.5 px-4 text-xs font-bold uppercase tracking-wider text-slate-400">Input $/1M</th>
                <th className="text-right py-2.5 px-4 text-xs font-bold uppercase tracking-wider text-slate-400">Output $/1M</th>
                <th className="text-center py-2.5 px-4 text-xs font-bold uppercase tracking-wider text-slate-400">Active</th>
                <th className="text-right py-2.5 px-4 w-24" />
              </tr>
            </thead>
            <tbody>
              {showAdd && (
                <tr className="border-b border-[#e2e8f0] bg-primary/5">
                  <td className="py-2 px-4">
                    <select
                      value={addForm.provider}
                      onChange={e => setAddForm({ ...addForm, provider: e.target.value })}
                      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                    >
                      {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <input
                      value={addForm.modelId}
                      onChange={e => setAddForm({ ...addForm, modelId: e.target.value })}
                      placeholder="e.g. gpt-4o"
                      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <input
                      value={addForm.displayName}
                      onChange={e => setAddForm({ ...addForm, displayName: e.target.value })}
                      placeholder="e.g. GPT-4o"
                      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number" step="0.01" value={addForm.inputPer1M}
                      onChange={e => setAddForm({ ...addForm, inputPer1M: parseFloat(e.target.value) || 0 })}
                      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number" step="0.01" value={addForm.outputPer1M}
                      onChange={e => setAddForm({ ...addForm, outputPer1M: parseFloat(e.target.value) || 0 })}
                      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right"
                    />
                  </td>
                  <td className="py-2 px-4 text-center">
                    <input type="checkbox" checked={addForm.active} onChange={e => setAddForm({ ...addForm, active: e.target.checked })} />
                  </td>
                  <td className="py-2 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={handleSaveNew} disabled={saving || !addForm.modelId.trim()} className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-30">
                        <Save size={14} />
                      </button>
                      <button onClick={() => setShowAdd(false)} className="p-1 text-slate-400 hover:bg-slate-50 rounded">
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map(entry => (
                <tr key={entry.modelId} className="border-b border-[#e2e8f0] hover:bg-slate-50/60 transition-colors">
                  {editingId === entry.modelId ? (
                    <>
                      <td className="py-2 px-4">
                        <select
                          value={editForm.provider}
                          onChange={e => setEditForm({ ...editForm, provider: e.target.value })}
                          className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                        >
                          {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-4 text-xs text-slate-500 font-mono">{entry.modelId}</td>
                      <td className="py-2 px-4">
                        <input
                          value={editForm.displayName}
                          onChange={e => setEditForm({ ...editForm, displayName: e.target.value })}
                          className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="number" step="0.01" value={editForm.inputPer1M}
                          onChange={e => setEditForm({ ...editForm, inputPer1M: parseFloat(e.target.value) || 0 })}
                          className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="number" step="0.01" value={editForm.outputPer1M}
                          onChange={e => setEditForm({ ...editForm, outputPer1M: parseFloat(e.target.value) || 0 })}
                          className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right"
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <input type="checkbox" checked={editForm.active} onChange={e => setEditForm({ ...editForm, active: e.target.checked })} />
                      </td>
                      <td className="py-2 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={handleSaveEdit} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-30">
                            <Save size={14} />
                          </button>
                          <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2.5 px-4">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                          entry.provider === 'anthropic' ? 'bg-orange-50 text-orange-700' :
                          entry.provider === 'openai' ? 'bg-emerald-50 text-emerald-700' :
                          entry.provider === 'google' ? 'bg-blue-50 text-blue-700' :
                          entry.provider === 'mistral' ? 'bg-purple-50 text-purple-700' :
                          'bg-slate-50 text-slate-600'
                        }`}>{entry.provider}</span>
                      </td>
                      <td className="py-2.5 px-4 text-xs font-mono text-slate-600">{entry.modelId}</td>
                      <td className="py-2.5 px-4 text-navy">{entry.displayName}</td>
                      <td className="py-2.5 px-4 text-right font-medium text-navy">${entry.inputPer1M.toFixed(2)}</td>
                      <td className="py-2.5 px-4 text-right font-medium text-navy">${entry.outputPer1M.toFixed(2)}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${entry.active ? 'bg-green-500' : 'bg-slate-300'}`} />
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleStartEdit(entry)} className="p-1 text-slate-400 hover:text-primary hover:bg-primary/5 rounded transition-colors">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => handleDelete(entry.modelId)} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && !showAdd && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400 text-sm">
                    {entries.length === 0
                      ? 'No pricing entries. Click "Seed Defaults" to load standard model pricing.'
                      : 'No models match the selected filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SHARED
// ============================================================================

function SummaryCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
        <Icon size={18} className="text-primary" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-navy">{value}</p>
      </div>
    </div>
  );
}

export default LLMCostsAdmin;
